/**
 * Phase 61 Plan 06 — TanStack Query hooks for the respect admin page.
 *
 * All mutations invalidate:
 *   - ['respect-admin', 'category-tags'] — admin list (includes disabled)
 *   - ['kudos', 'category-tags'] — public give-kudos picker (Plan 05) so
 *     newly created or re-enabled tags appear immediately in the modal.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useErrorToast } from './queryUtils';
import {
  listCategoryTags,
  createCategoryTag,
  updateCategoryTag,
  disableCategoryTag,
  reenableCategoryTag,
  getWeights,
  updateWeights,
  previewWeightImpact,
} from '../services/api/respectAdmin';
import type {
  CreateCategoryTagInput,
  UpdateCategoryTagInput,
  UpdateWeightsInput,
  PreviewWeightInput,
} from '../services/api/respectAdmin';

// ── Category tag queries + mutations ─────────────────────────────────────────

export function useCategoryTags() {
  return useQuery({
    queryKey: ['respect-admin', 'category-tags'],
    queryFn: () => listCategoryTags().then((r) => r.data),
  });
}

export function useCreateCategoryTag() {
  const qc = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: (input: CreateCategoryTagInput) => createCategoryTag(input).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['respect-admin', 'category-tags'] });
      qc.invalidateQueries({ queryKey: ['kudos', 'category-tags'] });
    },
    onError: (err) => onError(err, "Couldn't create category tag"),
  });
}

export function useUpdateCategoryTag() {
  const qc = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: UpdateCategoryTagInput }) =>
      updateCategoryTag(id, input).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['respect-admin', 'category-tags'] });
      qc.invalidateQueries({ queryKey: ['kudos', 'category-tags'] });
    },
    onError: (err) => onError(err, "Couldn't update category tag"),
  });
}

export function useDisableCategoryTag() {
  const qc = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: (id: number) => disableCategoryTag(id).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['respect-admin', 'category-tags'] });
      qc.invalidateQueries({ queryKey: ['kudos', 'category-tags'] });
    },
    onError: (err) => onError(err, "Couldn't disable category tag"),
  });
}

export function useReenableCategoryTag() {
  const qc = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: (id: number) => reenableCategoryTag(id).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['respect-admin', 'category-tags'] });
      qc.invalidateQueries({ queryKey: ['kudos', 'category-tags'] });
    },
    onError: (err) => onError(err, "Couldn't re-enable category tag"),
  });
}

// ── Weight setting queries + mutations ───────────────────────────────────────

export function useRespectWeights() {
  return useQuery({
    queryKey: ['respect-admin', 'weights'],
    queryFn: () => getWeights().then((r) => r.data),
  });
}

export function useUpdateWeights() {
  const qc = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: (input: UpdateWeightsInput) => updateWeights(input).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['respect-admin', 'weights'] });
    },
    onError: (err) => onError(err, "Couldn't update weights"),
  });
}

// ── Preview (D-22 read-only) ─────────────────────────────────────────────────

export function usePreviewWeightImpact() {
  const onError = useErrorToast();
  return useMutation({
    mutationFn: (input: PreviewWeightInput) =>
      previewWeightImpact(input).then((r) => r.data),
    onError: (err) => onError(err, "Couldn't compute preview"),
  });
}
